import { type NewCoreIconProps } from '@atlaskit/icon';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CheckboxCheckedIcon from '@atlaskit/icon/core/checkbox-checked';
import AtlaskitDatabaseIcon from '@atlaskit/icon/core/database';
import NumberIcon from '@atlaskit/icon/core/hashtag';
import ImageIcon from '@atlaskit/icon/core/image';
import LinkIcon from '@atlaskit/icon/core/link';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import PageIcon from '@atlaskit/icon/core/page';
import PersonIcon from '@atlaskit/icon/core/person';
import NewTagIcon from '@atlaskit/icon/core/tag';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import ArrowsDiagonalUpRightDownLeftIcon from '@atlaskit/icon-lab/core/arrows-diagonal-up-right-down-left';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import LozengeIcon from '@atlaskit/icon-lab/core/lozenge';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import { DatabaseFieldType } from '@databases/types/database';
import React, { type ComponentType, type FunctionComponent } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl-next';
import styled from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

const TWO_WAY_SYNC_JIRA_FIELDS = ['summary', 'status', 'priority', 'assignee'];

const TWO_WAY_SYNC_TYPES = [DatabaseFieldType.PAGE_STATUS, DatabaseFieldType.PAGE_LABELS];

const ONE_WAY_SYNC_TYPES = [
  DatabaseFieldType.PAGE_DETAILS,
  DatabaseFieldType.ENTRY_DETAILS,
];

const PAGE_FIELD_TYPES: DatabaseFieldType[] = [
  DatabaseFieldType.PAGE_STATUS,
  DatabaseFieldType.PAGE_LABELS,
  DatabaseFieldType.PAGE_DETAILS,
];

const ENTRY_FIELD_TYPES: DatabaseFieldType[] = [
  DatabaseFieldType.ENTRY_DETAILS,
  DatabaseFieldType.BACKLINK,
];

export interface DatabaseFieldIconType {
  icon: ComponentType<NewCoreIconProps>;
}

export const FIELD_ICONS: { [Type in DatabaseFieldType]: DatabaseFieldIconType } = {
  [DatabaseFieldType.TEXT]: {
    icon: AlignTextLeftIcon,
  },
  [DatabaseFieldType.ATTACHMENT]: {
    icon: ImageIcon,
  },
  [DatabaseFieldType.NUMBER]: {
    icon: NumberIcon,
  },
  [DatabaseFieldType.DATE]: {
    icon: CalendarIcon,
  },
  [DatabaseFieldType.SELECT]: {
    icon: NewTagIcon,
  },
  [DatabaseFieldType.USER]: {
    icon: PersonIcon,
  },
  [DatabaseFieldType.HYPERLINK]: {
    icon: LinkIcon,
  },
  [DatabaseFieldType.CHECKBOX]: {
    icon: CheckboxCheckedIcon,
  },
  [DatabaseFieldType.PAGE_LINK]: {
    icon: PageIcon,
  },
  [DatabaseFieldType.PAGE_STATUS]: {
    icon: CheckCircleIcon,
  },
  [DatabaseFieldType.PAGE_LABELS]: {
    icon: LozengeIcon,
  },
  [DatabaseFieldType.PAGE_DETAILS]: {
    icon: BookOpenIcon,
  },
  [DatabaseFieldType.ENTRY_LINK]: {
    icon: AtlaskitDatabaseIcon,
  },
  [DatabaseFieldType.ENTRY_DETAILS]: {
    icon: ListBulletedIcon,
  },
  [DatabaseFieldType.BACKLINK]: {
    icon: AtlaskitDatabaseIcon,
  },
  [DatabaseFieldType.JIRA_ISSUE]: {
    icon: WorkItemIcon,
  },
  [DatabaseFieldType.JIRA_ISSUE_DETAILS]: {
    icon: BookOpenIcon,
  },
};

const IconWithTooltip = styled.span`
  position: relative;
  display: inline-flex;
  pointer-events: auto;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 100%;
    margin-top: ${token('space.100')};
    left: 50%;
    transform: translateX(-50%);
    padding: ${token('space.050')} ${token('space.100')};
    background: ${token('color.background.neutral.bold')};
    color: ${token('color.text.inverse')};
    font: ${token('font.body.small')};
    border-radius: 3px;
    white-space: nowrap;
    visibility: hidden;
    z-index: 9999;
  }

  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
`;

export interface FieldTypeIconProps {
  type: DatabaseFieldType;
  label?: MessageDescriptor;
  noTooltip?: boolean;
  jiraField?: string;
  showSyncMessage?: boolean;
}

export const FieldTypeIcon: FunctionComponent<FieldTypeIconProps> = ({
  type,
  label,
  noTooltip,
  jiraField,
  showSyncMessage: showSyncIndicator,
}) => {
  const { formatMessage } = useIntl();

  const isSliceExperimentEnabled = getExperimentValue(
    'databasesJiraSliceAndDice',
    'enabled',
    { fireExperimentExposure: true }
  );

  const isJiraIssueDetails = type === DatabaseFieldType.JIRA_ISSUE_DETAILS;

  const hasJiraFieldSelected = isJiraIssueDetails && jiraField;
  const isJiraTwoWaySync =
    hasJiraFieldSelected && TWO_WAY_SYNC_JIRA_FIELDS.includes(jiraField);

  const getIconComponent = () => {
    if (isSliceExperimentEnabled) {
      if (hasJiraFieldSelected) {
        return isJiraTwoWaySync ? ArrowsDiagonalUpRightDownLeftIcon : ArrowUpRightIcon;
      }
      if (isJiraIssueDetails || TWO_WAY_SYNC_TYPES.includes(type)) {
        return ArrowsDiagonalUpRightDownLeftIcon;
      }
      if (ONE_WAY_SYNC_TYPES.includes(type)) {
        return ArrowUpRightIcon;
      }
    }
    return FIELD_ICONS[type].icon;
  };

  const IconComponent = getIconComponent();

  const icon = <IconComponent label="" testId={`field-type-icon-${type}`} />;

  const getTooltipContent = (): string | undefined => {
    if (hasJiraFieldSelected) {
      return TWO_WAY_SYNC_JIRA_FIELDS.includes(jiraField)
        ? formatMessage(messages['field-type-icon.two-way-synced'], { source: 'Jira' })
        : formatMessage(messages['field-type-icon.sourced-from'], { source: 'Jira' });
    }
    if (PAGE_FIELD_TYPES.includes(type)) {
      return TWO_WAY_SYNC_TYPES.includes(type)
        ? formatMessage(messages['field-type-icon.two-way-synced-with-confluence'])
        : formatMessage(messages['field-type-icon.sourced-from-confluence']);
    }
    if (ENTRY_FIELD_TYPES.includes(type)) {
      return formatMessage(messages['field-type-icon.sourced-from-database']);
    }
    return undefined;
  };

  if (isSliceExperimentEnabled && showSyncIndicator) {
    const tooltipContent = getTooltipContent();

    if (tooltipContent) {
      return (
        <IconWithTooltip
          data-tooltip={tooltipContent}
          data-testid="field-type-icon-tooltip"
        >
          {icon}
        </IconWithTooltip>
      );
    }
  }

  if (noTooltip || !label) {
    return icon;
  }

  const formattedLabel = formatMessage(label);

  return (
    <Tooltip tag="span" content={formattedLabel}>
      {icon}
    </Tooltip>
  );
};
